import type { MouseEvent } from "react";
import { AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { DefectCardActions } from "./DefectCardActions";
import { DefectCardBadges } from "./DefectCardBadges";
import { DefectCardDetails } from "./DefectCardDetails";
import { useDefectCardState } from "./useDefectCardState";
import type { DefectCardData, DefectCardProps } from "./DefectCard.types";

export type { DefectCardData } from "./DefectCard.types";

/** TES-1998: Klick auf die Karte öffnet den Mangel direkt (kein Menü-Umweg). TES-2042: der
 *  Karten-Klick nimmt bevorzugt `onOpen` (z. B. direkt in den Edit-Modus), sonst `onView`.
 *  Interaktive Nachfahren (Buttons, Links, Checkbox, Menü) behalten ihre eigene Aktion —
 *  der closest()-Guard lässt deren Klicks nicht zur Karte durchfallen. Tastatur-Zugang
 *  bleibt über das Aktionsmenü ("Details anzeigen") erhalten. */
function cardViewInteraction(
  defect: DefectCardData,
  cardClassName: string,
  onView?: (defect: DefectCardData) => void,
  onOpen?: (defect: DefectCardData) => void,
): { className: string; onClick?: (e: MouseEvent<HTMLDivElement>) => void } {
  const onActivate = onOpen ?? onView;
  if (!onActivate) return { className: cardClassName };
  return {
    className: cn(cardClassName, "cursor-pointer"),
    onClick: (e) => {
      const el = e.target as HTMLElement;
      if (el.closest("button, a, input, [role='checkbox'], [role='menuitem']")) return;
      onActivate(defect);
    },
  };
}

export function DefectCard({
  defect,
  selectable = false,
  selected = false,
  onSelectionChange,
  showMenu = true,
  onView,
  onOpen,
  onEdit,
  onResolve,
  onVerify,
  onStartProgress,
  onCancel,
  onReopen,
  onDelete,
  inspectionStatus,
  currentUserId,
  onAssign,
  assignableUsers = [],
  compact = false,
  isQualitaetsmanager = false,
  currentUserTeamIds,
  readOnly = false,
}: DefectCardProps) {
  const { t } = useTranslation();
  const { isOverdue, cardClassName } = useDefectCardState(defect, selected);
  const cardInteraction = cardViewInteraction(defect, cardClassName, onView, onOpen);

  return (
    <Card {...cardInteraction} data-testid={`defect-row-${defect.id}`}>
      <CardContent className={compact ? "p-4" : "p-4 sm:p-6"}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              {selectable && (
                <Checkbox
                  checked={selected}
                  onCheckedChange={() => onSelectionChange?.(defect.id)}
                  onClick={(e) => e.stopPropagation()}
                  className="mt-1"
                  data-testid={`defect-select-${defect.id}`}
                  aria-label={t("defects.selectDefect", { title: defect.title })}
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex flex-col gap-2">
                  <div className="flex items-start gap-3">
                    <div className="flex items-center justify-center h-7 w-7 rounded-md bg-warning/10 text-warning shrink-0">
                      <AlertCircle className="h-4 w-4" />
                    </div>
                    {/* TES-2229 S5 (Kommentar 2): stornierte Karte durchgestrichen + „⊘ Storniert"-Flag. */}
                    <h3
                      className={cn(
                        "font-semibold break-words",
                        compact ? "text-base" : "text-lg",
                        defect.status === "cancelled" && "line-through text-muted-foreground",
                      )}
                    >
                      {defect.title}
                    </h3>
                    {defect.status === "cancelled" && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full border border-muted-foreground/30 bg-muted px-2 py-0.5 text-xs text-muted-foreground shrink-0"
                        data-testid={`defect-cancelled-flag-${defect.id}`}
                      >
                        {"⊘"} {t("defects.status.cancelled")}
                      </span>
                    )}
                  </div>

                  <DefectCardBadges
                    defect={defect}
                    isOverdue={isOverdue}
                    assignableUsers={assignableUsers}
                    onAssign={onAssign}
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 shrink-0 ml-10 sm:ml-0">
              <DefectCardActions
                defect={defect}
                showMenu={showMenu}
                onView={onView}
                onEdit={onEdit}
                onResolve={onResolve}
                onVerify={onVerify}
                onStartProgress={onStartProgress}
                onCancel={onCancel}
                onReopen={onReopen}
                onDelete={onDelete}
                inspectionStatus={inspectionStatus}
                currentUserId={currentUserId}
                onAssign={onAssign}
                assignableUsers={assignableUsers}
                isQualitaetsmanager={isQualitaetsmanager}
                currentUserTeamIds={currentUserTeamIds}
                readOnly={readOnly}
              />
            </div>
          </div>

          <DefectCardDetails
            defect={defect}
            inspectionStatus={inspectionStatus}
            currentUserId={currentUserId}
            compact={compact}
            isOverdue={isOverdue}
          />
        </div>
      </CardContent>
    </Card>
  );
}
